import React, { Fragment, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import { getParamConditions } from "shared/lib/golCopy";
import {
  Condition,
  ConditionBlock,
} from "shared/lib/golObjectTypes/FlightConditions/FlightConditions";
import { formattedMessageParams } from "shared/messages";

import Img from "@components/FileServer/Img";
import { GetRefsForFlightOffer } from "@components/Flights/FlightAdditionalInfo";
import FormattedMessage from "@components/UI/FormattedMessage";
import LoaderMini from "@components/UI/LoaderMini";
import Modal from "@components/UI/Modal";

const { noConditions } = defineMessages({
  noConditions: formattedMessageParams("FlightConditions.noConditions"),
});

interface Props {
  getRefsForFlightOffer: GetRefsForFlightOffer;
  setIsSomeModalOpenned: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
}

interface State {
  isLoading: boolean;
  conditions: ConditionBlock[];
  selectedBlock: number;
  selectedCondition: number;
  error: boolean;
}

const FlightConditions = ({
  getRefsForFlightOffer,
  setIsSomeModalOpenned,
  onClose,
}: Props) => {
  const intl = useIntl();

  const [state, setState] = useState<State>({
    isLoading: true,
    conditions: [],
    selectedBlock: null,
    selectedCondition: null,
    error: false,
  });

  const [modalContent, setModalContent] = useState<{
    title: string;
    content: string;
  }>({
    title: null,
    content: null,
  });

  useEffect(() => {
    (async () => {
      const refs: string[] = getRefsForFlightOffer();
      const conditions: ConditionBlock[] = await getParamConditions(refs);
      if (!conditions || conditions.length === 0) {
        setState((currentState) => ({
          ...currentState,
          error: true,
          isLoading: false,
        }));
      } else {
        setState((currentState) => ({
          ...currentState,
          conditions: [...conditions],
          isLoading: false,
        }));
      }
    })();
  }, [getRefsForFlightOffer]);

  const modalFilled = !!modalContent.title && !!modalContent.content;

  const hideModal = () => {
    setModalContent({
      title: null,
      content: null,
    });
  };

  const selectCondition = (
    blockIndex: number,
    conditionIndex: number
  ): void => {
    setModalContent({
      title: state.conditions[blockIndex].Condition[conditionIndex].Title,
      content: state.conditions[blockIndex].Condition[conditionIndex].$t,
    });
  };

  const conditionsHeader = (
    <div className="flight-conditions-passager-type-title">
      <FormattedMessage id="FlightConditions.tariffConditions" />
      <Img
        src="/static/images/cross-blue-big.svg"
        className="flight-conditions-close-btn"
        onClick={onClose}
      />
    </div>
  );

  const errorRes = intl.formatMessage(noConditions);

  const loadingRes = (
    <div className="flight-conditions-loading">
      <LoaderMini />
    </div>
  );

  const conditionsRes = state.conditions.map(
    (block: ConditionBlock, blockIndex: number) => (
      <Fragment key={blockIndex}>
        <div
          data-cy={`flight-conditions-tarif-${blockIndex}`}
          className="flight-conditions-tarif-title-container"
        >
          <div>
            <span className="flight-conditions-tarif-title">
              <FormattedMessage id="FlightConditions.tariff" />:{" "}
              {block.BasisCode}
            </span>
          </div>
        </div>
        <div className="flight-conditions-condition">
          {block.Condition.map(
            (condition: Condition, conditionIndex: number) => (
              <div
                key={conditionIndex}
                role="button"
                className="link pointer"
                onClick={() => selectCondition(blockIndex, conditionIndex)}
              >
                {condition.Title}
              </div>
            )
          )}
        </div>
      </Fragment>
    )
  );

  useEffect(() => {
    if (setIsSomeModalOpenned) {
      setIsSomeModalOpenned(modalFilled);
    }
    // eslint-disable-next-line
  }, [modalFilled]);

  const formateContent = (content: string) => {
    if (!content) return null;

    return content.split("\n").map((line, idx) => {
      if (line.includes("https://")) {
        const parts = line.split("https://");
        return (
          <div key={idx}>
            {parts[0]}
            <a
              href={`https://${parts[1]}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {parts[1]}
            </a>
          </div>
        );
      }
      return (
        <div key={idx}>
          <br />
          {line}
        </div>
      );
    });
  };

  return (
    <div className="flight-conditions">
      <div className="flight-conditions-list">
        {conditionsHeader}
        <div>
          {state.isLoading
            ? loadingRes
            : state.conditions.length > 0
            ? conditionsRes
            : errorRes}
        </div>
      </div>
      <Modal isActive={modalFilled} onClose={hideModal}>
        <div className="flight-contidions-content">
          <h3 className="flight-conditions-passager-type-title">
            {modalContent.title}
          </h3>
          <div>{formateContent(modalContent?.content)}</div>
        </div>
      </Modal>
    </div>
  );
};

export default FlightConditions;
